// Per-line "Editing shipping information" modal (SO→PO rebuild Round 1, T06 + T07).
//
// Two-leg edit form mirroring PoShippingSection's field grammar: a SUPPLIER
// leg (always) and a DECORATOR panel rendered ONLY when Ship To = Decorator —
// exactly commonsku's per-item shipping modal. Field order follows the PO
// editor: supplier + routing → ship-to → account/method → dates → quotes →
// notes. Save is a SOFT gate: the server (PATCH /orders/:id/lines/:line_id/
// shipping) validates the cross-document dates; on validation errors it returns
// 422 with findings grouped by document, and the modal offers "Continue
// editing" (primary) vs a de-emphasized "Save with errors" (resends
// save_with_errors). Data-fetching is TanStack Query; brand-locked.
//
// BULK MODE (T07): passed `bulkLineIds` instead of a `row`, the SAME form drives
// a bulk apply — one patch PATCHed across every selected line via
// /orders/:id/lines/shipping/bulk. The response is a per-line summary (NOT
// all-or-nothing); lines that soft-block on a validation error are listed and
// can be forced with "Save with errors", which resends the batch with the flag.
import { ShipMethodPicker } from "@/components/orders/ShipMethodPicker";
import { methodForAccount } from "@/lib/carriers";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Truck } from "lucide-react";
import {
  type ShippingFinding,
  groupShippingFindingsByDocument,
  validateLineItemShippingDates,
} from "@merchd/shared";
import { api, ApiError } from "@/lib/api";

interface ShipAddressSnapshot {
  label?: string;
  attention?: string | null;
  street1?: string;
  city?: string;
  stateCode?: string;
  postalCode?: string;
  [k: string]: unknown;
}

export interface LineShippingRow {
  lineItemId: string;
  lineNumber: number;
  serviceType: string;
  description: string | null;
  productSnapshot: { name?: string; vendorId?: string } | null;
  supplierVendorId: string | null;
  supplierVendorName: string | null;
  supplierShipDate: string | null;
  supplierShipDateFirm: boolean;
  shipToRouting: string;
  /** The client address picked for this item (null = none picked). */
  supplierShipToAddressId?: string | null;
  supplierShipToAddressSnapshot: unknown;
  supplierShippingAccountId: string | null;
  supplierShippingMethod: string | null;
  supplierShippingQuote: string | null;
  supplierNotes: string | null;
  supplierSalesQuote: string | null;
  decoratorVendorId: string | null;
  decoratorVendorName: string | null;
  decoratorFinalShipTo: string | null;
  decoratorShipDate: string | null;
  decoratorShipDateFirm: boolean;
  decoratorShippingMethod: string | null;
  decoratorShippingQuote: string | null;
  /** The decorator's shipping account (Step 2) — older API responses omit it. */
  decoratorShippingAccountId?: string | null;
  decoratorNotes: string | null;
  hasStagedRow: boolean;
  coveredByPoNumber: number | null;
  coveredByPoId: string | null;
  /** Who ships this line, as answered in the quote's shipping estimate
   *  (pilot #4a, 2026-10-14) — used to start an unplanned line. */
  quoteRoute?:
    | { kind: "direct" }
    | { kind: "decorator"; decoratorName: string; vendorId: string | null }
    | null;
}

interface AddressRow {
  id: string;
  label: string;
  street1: string;
  city: string;
  stateCode: string;
}
interface AccountRow {
  id: string;
  carrier: string;
  accountNumber: string;
  label: string | null;
  isActive: boolean | null;
}
interface VendorRow {
  id: string;
  name: string;
  capabilities?: string[] | null;
}

// "Decorator" isn't a Ship-to choice any more: picking "3rd-party decorator"
// under "Who decorates?" is what sends the blanks there (Stephanie 2026-10-15).
const ROUTING_OPTIONS: { value: string; label: string }[] = [
  { value: "client", label: "Client" },
  { value: "merchd", label: "Other" },
  { value: "other_supplier", label: "Other supplier" },
];

// Read-only destination copy for the non-client routings — the client-address
// picker is only meaningful when the supplier ships straight to the client.
const NON_CLIENT_DESTINATION: Record<string, string> = {
  decorator: "Ships to the decorator picked above.",
  merchd: "Ships to the Other warehouse.",
  other_supplier:
    "Ships to another supplier — record who in Supplier notes below.",
};

const inputCls =
  "mt-1 block w-full rounded-lg border border-brown-200 bg-white px-3 py-2 text-sm text-brown-500";
const labelCls =
  "block text-xs font-semibold uppercase tracking-wide text-brown-400";
const legHeadingCls = "font-display text-sm font-bold text-brown-500";

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string } | undefined;
    return body?.error ?? `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

/** One line for an address snapshot: "Main — 19542 Estuary Drive, Boca Raton FL". */
export function destinationText(snap: ShipAddressSnapshot): string {
  const place = [snap.street1, [snap.city, snap.stateCode].filter(Boolean).join(" ")].filter((p) => p && String(p).trim()).join(", ");
  const name = (snap.label ?? snap.attention ?? "").toString().trim();
  return [name, place].filter(Boolean).join(" — ") || "Saved address";
}

/** Is this client address the one already saved (same street + city)? */
function sameAddress(a: { street1: string; city: string }, snap: ShipAddressSnapshot | null): boolean {
  if (!snap) return false;
  const n = (v: unknown) => String(v ?? "").trim().toLowerCase();
  return n(a.street1) === n(snap.street1) && n(a.city) === n(snap.city);
}

/** One line explaining that the document labels on the findings name the POs
 *  this line will mint at Create-POs time (the labels themselves stay as-is). */
function FindingsIntro() {
  return (
    <p className="mb-2 text-xs text-brown-400">
      “Supplier PO” and “Decorator PO” below name the purchase orders this line
      will produce when you create POs.
    </p>
  );
}

function FindingGroups({ findings }: { findings: ShippingFinding[] }) {
  const groups = groupShippingFindingsByDocument(findings);
  if (groups.length === 0) return null;
  return (
    <div className="space-y-2">
      {groups.map((g) => (
        <div key={g.document}>
          <p className="text-xs font-semibold uppercase tracking-wide text-brown-400">
            {g.document}
          </p>
          <ul className="mt-1 space-y-1">
            {g.findings.map((f) => (
              <li
                key={f.code}
                className={
                  f.severity === "error"
                    ? "text-sm text-rust-600"
                    : "text-sm text-brown-400"
                }
              >
                {f.severity === "error" ? "Error: " : "Warning: "}
                {f.message}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

interface BulkResult {
  line_item_id: string;
  saved: boolean;
  errors: ShippingFinding[];
  warnings: ShippingFinding[];
  error?: string;
}
interface BulkResponse {
  saved_count: number;
  total: number;
  results: BulkResult[];
}

// An empty row supplies the bulk-mode form defaults (no single line to seed from).
export const EMPTY_ROW: LineShippingRow = {
  lineItemId: "",
  lineNumber: 0,
  serviceType: "product",
  description: null,
  productSnapshot: null,
  supplierVendorId: null,
  supplierVendorName: null,
  supplierShipDate: null,
  supplierShipDateFirm: false,
  shipToRouting: "client",
  supplierShipToAddressId: null,
  supplierShipToAddressSnapshot: null,
  supplierShippingAccountId: null,
  supplierShippingMethod: null,
  supplierShippingQuote: null,
  supplierNotes: null,
  supplierSalesQuote: null,
  decoratorVendorId: null,
  decoratorVendorName: null,
  decoratorFinalShipTo: null,
  decoratorShipDate: null,
  decoratorShipDateFirm: false,
  decoratorShippingMethod: null,
  decoratorShippingQuote: null,
  decoratorShippingAccountId: null,
  decoratorNotes: null,
  hasStagedRow: false,
  coveredByPoNumber: null,
  coveredByPoId: null,
};

/** Bulk mode: what the selected items already share, so the window opens
 *  showing it (Stephanie 2026-10-27: "when i went into edit the shipping info,
 *  nothing i had entered shows"). A field the items disagree on starts blank
 *  and, left alone, keeps each item's own value. */
export function commonShippingRow(rows: readonly LineShippingRow[]): LineShippingRow {
  if (rows.length === 0) return EMPTY_ROW;
  const out: Record<string, unknown> = { ...EMPTY_ROW };
  for (const key of Object.keys(EMPTY_ROW) as Array<keyof LineShippingRow>) {
    if (key === "lineItemId" || key === "lineNumber" || key === "description" || key === "productSnapshot" || key === "coveredByPoNumber" || key === "coveredByPoId") continue;
    const first = JSON.stringify(rows[0]![key] ?? null);
    if (rows.every((r) => JSON.stringify(r[key] ?? null) === first)) out[key] = rows[0]![key] ?? EMPTY_ROW[key];
  }
  return out as unknown as LineShippingRow;
}

export function LineItemShippingModal(props: {
  orderId: string;
  clientId: string;
  inHandsDate: string | null;
  /** Single-line mode: the row being edited. */
  row?: LineShippingRow;
  /** Bulk mode (T07): apply one patch across these line ids. */
  bulkLineIds?: string[];
  /** Bulk mode (D9): line id → product label, so the per-line result summary
   *  names each item instead of showing a bare id. */
  bulkLabels?: Record<string, string>;
  /** Bulk mode (D9): open the single-line editor for a failed line ("Fix →"). */
  onFixLine?: (lineId: string) => void;
  /** Bulk mode: the supplier every selected item shares (its own product's
   *  supplier, or the one already saved) — pre-picked (Stephanie 2026-10-14). */
  bulkSupplierVendorId?: string | null;
  /** Bulk mode: the values every selected item shares (commonShippingRow). */
  bulkCommonRow?: LineShippingRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { orderId, clientId, inHandsDate, bulkLineIds, bulkLabels } = props;
  const isBulk = Array.isArray(bulkLineIds) && bulkLineIds.length > 0;
  const labelFor = (id: string): string => bulkLabels?.[id] ?? "Item";
  const row = props.row ?? (isBulk ? props.bulkCommonRow : undefined) ?? EMPTY_ROW;
  const lineName = isBulk
    ? `${bulkLineIds!.length} item${bulkLineIds!.length === 1 ? "" : "s"}`
    : row.productSnapshot?.name?.trim() ||
      row.description?.trim() ||
      `Line ${row.lineNumber}`;
  const currentDestination =
    (row.supplierShipToAddressSnapshot ?? null) as ShipAddressSnapshot | null;
  const savedClientDestination = row.shipToRouting === "client" ? currentDestination : null;

  // Start an unplanned line from the quote's "who ships" answer (pilot #4a).
  const fromQuote = !row.hasStagedRow ? (row.quoteRoute ?? null) : null;
  const [routing, setRouting] = useState(
    fromQuote?.kind === "decorator" ? "decorator" : row.shipToRouting || "client",
  );
  // Pre-pick the item's own supplier when nothing is staged yet (pilot #4).
  const [supplierVendorId, setSupplierVendorId] = useState(
    isBulk
      ? (props.bulkSupplierVendorId ?? "")
      : (row.supplierVendorId ?? row.productSnapshot?.vendorId ?? ""),
  );
  const [isSample, setIsSample] = useState(false);
  const [supplierShipDate, setSupplierShipDate] = useState(row.supplierShipDate ?? "");
  const [supplierShipDateFirm, setSupplierShipDateFirm] = useState(row.supplierShipDateFirm);
  // Opens on the address already picked (Stephanie 2026-11-02: it said "Pick an
  // address…" instead of what she'd selected — and saving then cleared it).
  const [supplierAddressId, setSupplierAddressId] = useState(row.supplierShipToAddressId ?? "");
  const [supplierAccountId, setSupplierAccountId] = useState(row.supplierShippingAccountId ?? "");
  const [supplierMethod, setSupplierMethod] = useState(row.supplierShippingMethod ?? "");
  const [supplierQuote, setSupplierQuote] = useState(row.supplierShippingQuote ?? "");
  const [supplierNotes, setSupplierNotes] = useState(row.supplierNotes ?? "");

  const [decoratorVendorId, setDecoratorVendorId] = useState(
    row.decoratorVendorId ?? (fromQuote?.kind === "decorator" ? (fromQuote.vendorId ?? "") : ""),
  );
  const [decoratorFinalShipTo, setDecoratorFinalShipTo] = useState(
    row.decoratorFinalShipTo ?? "client",
  );
  const [decoratorFinalAddressId, setDecoratorFinalAddressId] = useState("");
  const [decoratorShipDate, setDecoratorShipDate] = useState(row.decoratorShipDate ?? "");
  const [decoratorShipDateFirm, setDecoratorShipDateFirm] = useState(row.decoratorShipDateFirm);
  const [decoratorMethod, setDecoratorMethod] = useState(row.decoratorShippingMethod ?? "");
  const [decoratorAccountId, setDecoratorAccountId] = useState(row.decoratorShippingAccountId ?? "");
  const [decoratorQuote, setDecoratorQuote] = useState(row.decoratorShippingQuote ?? "");
  const [decoratorNotes, setDecoratorNotes] = useState(row.decoratorNotes ?? "");

  const [error, setError] = useState<string | null>(null);
  const [gate, setGate] = useState<{
    errors: ShippingFinding[];
    warnings: ShippingFinding[];
  } | null>(null);
  const [bulkSummary, setBulkSummary] = useState<BulkResponse | null>(null);

  const addresses = useQuery({
    queryKey: ["clients", clientId, "addresses"],
    queryFn: () => api<{ addresses: AddressRow[] }>(`/clients/${clientId}/addresses`),
  });
  const accounts = useQuery({
    queryKey: ["clients", clientId, "shipping-accounts"],
    queryFn: () =>
      api<{ shipping_accounts: AccountRow[] }>(`/clients/${clientId}/shipping-accounts`),
  });
  const vendors = useQuery({
    queryKey: ["vendors", "list"],
    queryFn: () => api<{ rows: VendorRow[] }>(`/vendors`),
  });
  // Other's own accounts + "Ship on supplier's account" (2026-10-14).
  const house = useQuery({
    queryKey: ["shipping-accounts", "house"],
    queryFn: () =>
      api<{
        ready: boolean;
        shipping_accounts: Array<{ id: string; carrier: string; accountNumber: string; label: string | null; kind: "other" | "supplier" }>;
      }>(`/shipping-accounts/house`),
    staleTime: 5 * 60 * 1000,
  });
  const houseRows = house.data?.shipping_accounts ?? [];

  const addressRows = addresses.data?.addresses ?? [];
  const accountRows = (accounts.data?.shipping_accounts ?? []).filter(
    (a) => a.isActive !== false,
  );
  const vendorRows = vendors.data?.rows ?? [];
  // Decorator drop-down: only vendors tagged as decorators (falls back to
  // everyone if none are tagged yet, so the choice is never empty).
  const taggedDecorators = vendorRows.filter((v) => (v.capabilities ?? []).includes("provides_decoration"));
  const decoratorRows = taggedDecorators.length > 0 ? taggedDecorators : vendorRows;

  const liveFindings = useMemo(
    () =>
      validateLineItemShippingDates({
        inHandsDate,
        supplierShipDate: supplierShipDate || null,
        routing,
        decoratorShipDate: decoratorShipDate || null,
      }),
    [inHandsDate, supplierShipDate, routing, decoratorShipDate],
  );

  function buildBody(saveWithErrors: boolean): Record<string, unknown> {
    return {
      supplier_vendor_id: supplierVendorId || null,
      is_sample: isSample,
      supplier_ship_date: supplierShipDate || null,
      supplier_ship_date_firm: supplierShipDateFirm,
      ship_to_routing: routing,
      // Only a client routing carries a client-address ship-to; the other
      // routings resolve their destination from the vendor/decorator.
      supplier_ship_to_address_id:
        routing === "client" ? supplierAddressId || null : null,
      supplier_shipping_account_id: supplierAccountId || null,
      supplier_shipping_method: supplierMethod.trim() || null,
      supplier_shipping_quote: supplierQuote.trim() || null,
      supplier_notes: supplierNotes.trim() || null,
      decorator_vendor_id: decoratorVendorId || null,
      decorator_final_ship_to: decoratorFinalShipTo || null,
      decorator_final_address_id: decoratorFinalAddressId || null,
      decorator_ship_date: decoratorShipDate || null,
      decorator_ship_date_firm: decoratorShipDateFirm,
      decorator_shipping_account_id: decoratorAccountId || null,
      decorator_shipping_method: decoratorMethod.trim() || null,
      decorator_shipping_quote: decoratorQuote.trim() || null,
      decorator_notes: decoratorNotes.trim() || null,
      save_with_errors: saveWithErrors,
    };
  }

  const save = useMutation({
    mutationFn: (saveWithErrors: boolean) =>
      api(`/orders/${orderId}/lines/${row.lineItemId}/shipping`, {
        method: "PATCH",
        body: JSON.stringify(buildBody(saveWithErrors)),
      }),
    onSuccess: () => {
      setGate(null);
      setError(null);
      props.onSaved();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        const body = e.body as {
          errors?: ShippingFinding[];
          warnings?: ShippingFinding[];
        };
        setGate({ errors: body.errors ?? [], warnings: body.warnings ?? [] });
        return;
      }
      setError(errText(e));
    },
  });

  // Bulk is a PARTIAL patch (D8): capture the form's mount-time baseline (all
  // empty defaults) once, then send ONLY the fields the rep actually changed —
  // untouched fields are omitted so the server preserves each divergent line's
  // existing value instead of clobbering it. save_with_errors always rides along.
  const bulkBaseline = useRef<Record<string, unknown> | null>(null);
  if (bulkBaseline.current === null) {
    bulkBaseline.current = buildBody(false);
    // A pre-picked supplier still has to be SENT — items with nothing saved
    // yet have no supplier on their shipping row.
    if (props.bulkSupplierVendorId) bulkBaseline.current.supplier_vendor_id = null;
  }
  function buildBulkBody(saveWithErrors: boolean): Record<string, unknown> {
    const full = buildBody(saveWithErrors);
    const base = bulkBaseline.current ?? {};
    const touched: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(full)) {
      if (k === "save_with_errors" || !Object.is(v, base[k])) touched[k] = v;
    }
    return { ...touched, line_item_ids: bulkLineIds ?? [] };
  }

  // Bulk apply — one patch across every selected line. The endpoint always
  // returns 200 with a per-line summary (soft-blocked lines carry saved:false),
  // so we render the summary here rather than routing through the 422 gate.
  const bulkSave = useMutation({
    mutationFn: (saveWithErrors: boolean) =>
      api<BulkResponse>(`/orders/${orderId}/lines/shipping/bulk`, {
        method: "PATCH",
        body: JSON.stringify(buildBulkBody(saveWithErrors)),
      }),
    onSuccess: (data) => {
      setError(null);
      // Everything applied cleanly → close straight away; otherwise surface the
      // per-line summary so the rep can force the soft-blocked lines.
      if (data.results.every((r) => r.saved)) {
        props.onSaved();
        return;
      }
      setBulkSummary(data);
    },
    onError: (e) => setError(errText(e)),
  });

  // "Who decorates?" (Stephanie 2026-10-15): a 3rd-party decorator means the
  // blanks ship to them (Step 1) and they ship the finished goods (Step 2,
  // its own PO). The supplier decorating is one step.
  const showDecorator = routing === "decorator";
  const pickDecorates = (third: boolean): void => {
    if (third) setRouting("decorator");
    else if (routing === "decorator") setRouting("client");
  };
  const supplierName = vendorRows.find((v) => v.id === supplierVendorId)?.name ?? "Supplier";
  const decoratorName = vendorRows.find((v) => v.id === decoratorVendorId)?.name ?? "the decorator";
  // The carrier of a chosen account (client's or other's), so the Carrier box can follow it.
  const accountCarrier = (id: string): string | null =>
    accountRows.find((a) => a.id === id)?.carrier ?? houseRows.find((a) => a.id === id)?.carrier ?? null;
  // The same account choices for both legs: client's, Other's, supplier's.
  const accountOptions = (
    <>
      <option value="">{house.data?.ready ? "Select…" : "Other / supplier account"}</option>
      {accountRows.length > 0 && (
        <optgroup label="Client's accounts">
          {accountRows.map((a) => (
            <option key={a.id} value={a.id}>
              #{a.accountNumber}
              {a.label ? ` (${a.label})` : ""}
            </option>
          ))}
        </optgroup>
      )}
      {houseRows.some((r) => r.kind === "other") && (
        <optgroup label="other's accounts">
          {houseRows.filter((r) => r.kind === "other").map((a) => (
            <option key={a.id} value={a.id}>
              {a.carrier.toUpperCase()} #{a.accountNumber}
              {a.label ? ` (${a.label})` : ""}
            </option>
          ))}
        </optgroup>
      )}
      {houseRows.filter((r) => r.kind === "supplier").map((a) => (
        <option key={a.id} value={a.id}>Ship on supplier&apos;s account</option>
      ))}
    </>
  );
  const bulkPending = bulkSave.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Editing shipping information"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-brown-50 px-6 py-4">
          <h2 className="inline-flex items-center gap-2 font-display text-lg font-bold text-brown-500">
            <Truck className="h-4 w-4 text-muted" /> Editing shipping information
          </h2>
          <p className="truncate text-sm text-brown-300">{lineName}</p>
        </div>

        {error && (
          <div className="mx-6 mt-4 rounded-lg border border-rust-200 bg-rust-50 px-4 py-2 text-sm text-rust-600">
            {error}
          </div>
        )}

        {gate ? (
          // ── Save gate: findings grouped by document ──────────────────────────
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <p className="mb-3 text-sm text-brown-500">
              This shipping setup has issues. Fix them, or save anyway.
            </p>
            <FindingsIntro />
            {gate.errors.length > 0 && (
              <div className="mb-4 rounded-lg border border-rust-200 bg-rust-50 px-4 py-3">
                <FindingGroups findings={gate.errors} />
              </div>
            )}
            {gate.warnings.length > 0 && (
              <div className="rounded-lg border border-brown-100 bg-cream-50 px-4 py-3">
                <FindingGroups findings={gate.warnings} />
              </div>
            )}
            <div className="mt-5 flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={() => save.mutate(true)}
                disabled={save.isPending}
                className="text-sm font-semibold text-ink underline-offset-2 hover:text-muted hover:underline disabled:opacity-50"
              >
                {save.isPending ? "Saving…" : "Save with errors"}
              </button>
              <button
                type="button"
                onClick={() => setGate(null)}
                className="rounded-lg bg-ink px-3.5 py-2 text-sm font-semibold text-white hover:bg-ink-700"
              >
                Continue editing
              </button>
            </div>
          </div>
        ) : bulkSummary ? (
          // ── Bulk apply summary: per-line, NOT all-or-nothing ─────────────────
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <p className="mb-3 text-sm font-semibold text-brown-500">
              {bulkSummary.saved_count} of {bulkSummary.total} items saved.
            </p>
            <ul className="space-y-1">
              {bulkSummary.results.map((r) => (
                <li key={r.line_item_id} className="text-sm">
                  {/* D9 — name each line by its product label. */}
                  <span className="font-semibold text-brown-500">
                    {labelFor(r.line_item_id)}
                  </span>{" "}
                  {r.saved ? (
                    <span className="text-brown-400">
                      — saved{r.warnings.length > 0 ? " (with warnings)" : ""}.
                    </span>
                  ) : (
                    <>
                      <span className="text-rust-600">
                        —{" "}
                        {r.error
                          ? `not saved (${r.error})`
                          : "needs attention (has validation errors)"}
                      </span>{" "}
                      {props.onFixLine && (
                        <button
                          type="button"
                          onClick={() => props.onFixLine!(r.line_item_id)}
                          className="font-semibold text-ink underline-offset-2 hover:text-muted hover:underline"
                        >
                          Fix →
                        </button>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
            {bulkSummary.results.some((r) => !r.saved && !r.error) && (
              <p className="mt-3 text-xs text-brown-400">
                Some items have date validation errors. Fix them, or save them
                anyway.
              </p>
            )}
            <div className="mt-5 flex items-center justify-end gap-4">
              {bulkSummary.results.some((r) => !r.saved && !r.error) && (
                <button
                  type="button"
                  onClick={() => bulkSave.mutate(true)}
                  disabled={bulkPending}
                  className="text-sm font-semibold text-ink underline-offset-2 hover:text-muted hover:underline disabled:opacity-50"
                >
                  {bulkPending ? "Saving…" : "Save with errors"}
                </button>
              )}
              <button
                type="button"
                onClick={() => props.onSaved()}
                className="rounded-lg bg-ink px-3.5 py-2 text-sm font-semibold text-white hover:bg-ink-700"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {isBulk && (
              <p className="rounded-lg border border-brown-100 bg-cream-50 px-4 py-2 text-sm text-brown-500">
                Filled-in boxes show what all {lineName} already share; a blank box
                means they differ. Only the boxes you change apply to all {lineName} —
                anything left as-is keeps each item's own value.
              </p>
            )}
            {/* ── Who decorates? (Stephanie 2026-10-15, like commonsku) ── */}
            <div>
              <p className={labelCls}>Who decorates?</p>
              <div role="radiogroup" aria-label="Who decorates" className="mt-1 flex w-fit rounded-full bg-canvas p-1">
                {(
                  [
                    [false, "Supplier decorates"],
                    [true, "3rd-party decorator"],
                  ] as const
                ).map(([third, label]) => (
                  <button
                    key={label}
                    type="button"
                    role="radio"
                    aria-checked={showDecorator === third}
                    onClick={() => pickDecorates(third)}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                      showDecorator === third ? "bg-ink text-white" : "text-muted hover:text-ink"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {showDecorator && (
              <label className={labelCls}>
                Decorator
                <select
                  value={decoratorVendorId}
                  onChange={(e) => setDecoratorVendorId(e.target.value)}
                  className={`${inputCls} font-normal normal-case`}
                >
                  <option value="">Select a decorator…</option>
                  {decoratorRows.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/* ── Step 1: the supplier's PO (blanks, or the finished goods) ── */}
            <div className="border-t border-brown-50 pt-4">
              <p className={legHeadingCls}>{showDecorator ? "Step 1 · Blanks" : "Supplier shipping"}</p>
              {showDecorator && (
                <p className="mt-0.5 text-sm text-brown-400">
                  {supplierName} → {decoratorName}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className={labelCls}>
                Supplier
                <select
                  value={supplierVendorId}
                  onChange={(e) => setSupplierVendorId(e.target.value)}
                  className={`${inputCls} font-normal normal-case`}
                >
                  <option value="">Select a supplier…</option>
                  {vendorRows.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
              {!showDecorator && (
                <label className={labelCls}>
                  Ship to
                  <select
                    value={routing}
                    onChange={(e) => setRouting(e.target.value)}
                    className={`${inputCls} font-normal normal-case`}
                  >
                    {ROUTING_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            {fromQuote && (
              <p className="text-xs text-muted">
                From the quote&apos;s shipping estimate:{" "}
                {fromQuote.kind === "decorator"
                  ? `goes to ${fromQuote.decoratorName} for decoration`
                  : "ships straight from the supplier"}
                . Change it here if the plan changed.
              </p>
            )}

            <label className="inline-flex items-center gap-2 text-sm text-brown-500">
              <input
                type="checkbox"
                checked={isSample}
                onChange={(e) => setIsSample(e.target.checked)}
              />
              This is a sample order
            </label>

            {/* Ship-to address — client-address picker only for routing=client;
                the other routings show a read-only destination note (X5). */}
            {routing === "client" ? (
              <div>
                <p className={labelCls}>Ship-to address</p>
                <select
                  value={supplierAddressId}
                  onChange={(e) => setSupplierAddressId(e.target.value)}
                  className={`${inputCls} font-normal normal-case`}
                >
                  {/* The saved address itself, not a "keep current" choice
                      (Stephanie 2026-10-27: "how are we supposed to know what
                      that is?"). Only a client destination counts as current. */}
                  <option value="">{savedClientDestination ? `${destinationText(savedClientDestination)} (saved)` : "Pick an address…"}</option>
                  {addressRows.filter((a) => !sameAddress(a, savedClientDestination)).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label} — {a.street1}, {a.city} {a.stateCode}
                    </option>
                  ))}
                </select>
              </div>
            ) : showDecorator ? null : (
              <div>
                <p className={labelCls}>Ship-to</p>
                <p className="mt-1 text-sm text-brown-400">
                  {NON_CLIENT_DESTINATION[routing]}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className={labelCls}>
                Shipping account
                <select
                  value={supplierAccountId}
                  onChange={(e) => {
                    setSupplierAccountId(e.target.value);
                    setSupplierMethod((m) => methodForAccount(m, accountCarrier(e.target.value)));
                  }}
                  className={`${inputCls} font-normal normal-case`}
                >
                  {accountOptions}
                </select>
              </label>
              <label className={labelCls}>
                Shipping method
                <ShipMethodPicker
                  value={supplierMethod}
                  onChange={setSupplierMethod}
                  selectClassName={`${inputCls} mt-0 font-normal normal-case`}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className={labelCls}>
                Supplier ship date
                <input
                  type="date"
                  value={supplierShipDate}
                  onChange={(e) => setSupplierShipDate(e.target.value)}
                  className={`${inputCls} font-normal normal-case`}
                />
                <span className="mt-1 inline-flex items-center gap-1.5 text-xs font-normal normal-case text-brown-400">
                  <input
                    type="checkbox"
                    checked={supplierShipDateFirm}
                    onChange={(e) => setSupplierShipDateFirm(e.target.checked)}
                  />
                  Firm date
                </span>
              </label>
              <div className={labelCls}>
                Client in-hands
                <p className="mt-1 py-2 text-sm font-normal normal-case text-brown-500">
                  {inHandsDate ?? "—"}
                </p>
              </div>
            </div>

            <label className={labelCls}>
              Shipping quote (cost)
              <input
                value={supplierQuote}
                onChange={(e) => setSupplierQuote(e.target.value)}
                placeholder="0.00"
                className={`${inputCls} font-normal normal-case`}
              />
            </label>

            <label className={labelCls}>
              Supplier notes
              <textarea
                value={supplierNotes}
                onChange={(e) => setSupplierNotes(e.target.value)}
                rows={2}
                placeholder="Using the notes field can slow down processing of the order."
                className={`${inputCls} font-normal normal-case`}
              />
            </label>

            {/* ── Step 2: the decorator's own PO (3rd-party decorator only) ── */}
            {showDecorator && (
              <div className="space-y-4 border-t border-brown-50 pt-4">
                <div>
                  <p className={legHeadingCls}>Step 2 · Decorated goods</p>
                  <p className="mt-0.5 text-sm text-brown-400">
                    {decoratorName} → {decoratorFinalShipTo === "merchd" ? "Other" : "Client"} · its own PO
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className={labelCls}>
                    Final ship to
                    <select
                      value={decoratorFinalShipTo}
                      onChange={(e) => setDecoratorFinalShipTo(e.target.value)}
                      className={`${inputCls} font-normal normal-case`}
                    >
                      <option value="client">Client</option>
                      <option value="merchd">other</option>
                    </select>
                  </label>
                </div>
                {decoratorFinalShipTo === "client" && (
                  <label className={labelCls}>
                    Final ship-to address
                    <select
                      value={decoratorFinalAddressId}
                      onChange={(e) => setDecoratorFinalAddressId(e.target.value)}
                      className={`${inputCls} font-normal normal-case`}
                    >
                      <option value="">Keep current / none</option>
                      {addressRows.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.label} — {a.street1}, {a.city} {a.stateCode}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className={labelCls}>
                    Shipping account
                    <select
                      value={decoratorAccountId}
                      onChange={(e) => {
                        setDecoratorAccountId(e.target.value);
                        setDecoratorMethod((m) => methodForAccount(m, accountCarrier(e.target.value)));
                      }}
                      className={`${inputCls} font-normal normal-case`}
                    >
                      {accountOptions}
                    </select>
                  </label>
                  <label className={labelCls}>
                    Shipping method
                    <ShipMethodPicker
                      value={decoratorMethod}
                      onChange={setDecoratorMethod}
                      selectClassName={`${inputCls} mt-0 font-normal normal-case`}
                    />
                  </label>
                  <label className={labelCls}>
                    Decorator ship date
                    <input
                      type="date"
                      value={decoratorShipDate}
                      onChange={(e) => setDecoratorShipDate(e.target.value)}
                      className={`${inputCls} font-normal normal-case`}
                    />
                    <span className="mt-1 inline-flex items-center gap-1.5 text-xs font-normal normal-case text-brown-400">
                      <input
                        type="checkbox"
                        checked={decoratorShipDateFirm}
                        onChange={(e) => setDecoratorShipDateFirm(e.target.checked)}
                      />
                      Firm date
                    </span>
                  </label>
                </div>
                <label className={labelCls}>
                  Shipping quote (cost)
                  <input
                    value={decoratorQuote}
                    onChange={(e) => setDecoratorQuote(e.target.value)}
                    placeholder="0.00"
                    className={`${inputCls} font-normal normal-case`}
                  />
                </label>
                <label className={labelCls}>
                  Decorator notes
                  <textarea
                    value={decoratorNotes}
                    onChange={(e) => setDecoratorNotes(e.target.value)}
                    rows={2}
                    className={`${inputCls} font-normal normal-case`}
                  />
                </label>
              </div>
            )}

            {liveFindings.length > 0 && (
              <div className="rounded-lg border border-brown-100 bg-cream-50 px-4 py-3">
                <FindingsIntro />
                <FindingGroups findings={liveFindings} />
              </div>
            )}
          </div>
        )}

        {!gate && !bulkSummary && (
          <div className="flex gap-2 border-t border-brown-50 px-6 py-4">
            <button
              type="button"
              onClick={() => (isBulk ? bulkSave.mutate(false) : save.mutate(false))}
              disabled={isBulk ? bulkPending : save.isPending}
              className="rounded-lg bg-ink px-3.5 py-2 text-sm font-semibold text-white hover:bg-ink-700 disabled:opacity-50"
            >
              {isBulk
                ? bulkPending
                  ? "Saving…"
                  : `Apply to ${lineName}`
                : save.isPending
                  ? "Saving…"
                  : "Save shipping"}
            </button>
            <button
              type="button"
              onClick={props.onClose}
              className="rounded-lg border border-brown-200 px-3.5 py-2 text-sm font-semibold text-brown-500 hover:bg-cream-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
